import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faArrowUpRightFromSquare,
  faBook,
  faCircleCheck,
  faClock,
  faComments,
  faGear,
  faLink,
  faPaperPlane,
  faRefresh,
  faRobot,
  faSpinner,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons"
import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { GenericDropDown } from "../../../components/Dropdown/Dropdown"
import InputField from "../../../components/InputField/InputField"
import { MetricCard } from "../../../components/MetricCard/MetricCard"
import { usePlatformWhatsApp } from "../../../hooks/PlatformWhatsApp/usePlatformWhatsApp"
import {
  emptyPlatformWhatsAppOverview,
  emptyPlatformWhatsAppSettings,
} from "../../../Objects/PlatformWhatsApp/PlatformWhatsApp"
import { type IPlatformWhatsAppPairingResult } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppPairingResult"
import { type IPlatformWhatsAppKnowledge } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppKnowledge"
import { type IPlatformWhatsAppMessage } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppMessage"
import { type IPlatformWhatsAppConversation } from "../../../Objects/PlatformWhatsApp/IPlatformWhatsAppConversation"
import { EPlatformWhatsAppConversationStatus } from "../../../Objects/PlatformWhatsApp/EPlatformWhatsAppConversationStatus"
import { EPlatformWhatsAppConversationStage } from "../../../Objects/PlatformWhatsApp/EPlatformWhatsAppConversationStage"
import { EWhatsAppTransportType } from "../../../Objects/PlatformWhatsApp/EWhatsAppTransportType"
import { PlatformWhatsAppAgentPopup } from "../PlatformWhatsApp/PlatformWhatsAppAgentPopup"
import { PlatformWhatsAppKnowledgeListPopup } from "../PlatformWhatsApp/PlatformWhatsAppKnowledgeListPopup"
import { PlatformWhatsAppKnowledgePopup } from "../PlatformWhatsApp/PlatformWhatsAppKnowledgePopup"
import { PlatformWhatsAppPairingPopup } from "../PlatformWhatsApp/PlatformWhatsAppPairingPopup"
import {
  PlatformWhatsAppRespostaAiSettingsPopup,
  RESPOSTA_AI_DEFAULT_BASE_URL,
} from "../PlatformWhatsApp/PlatformWhatsAppRespostaAiSettingsPopup"
import { PlatformWhatsAppSettingsPopup } from "../PlatformWhatsApp/PlatformWhatsAppSettingsPopup"

const emptyKnowledge = (): IPlatformWhatsAppKnowledge => ({
  id: 0,
  isSystem: false,
  title: "",
  category: "",
  content: "",
  keywords: "",
  priority: 50,
  isActive: true,
  version: 1,
})

export const PlatformWhatsAppSection = () => {
  const { t } = useTranslation("platformWhatsApp")

  const api = usePlatformWhatsApp()

  const [overview, setOverview] = useState(emptyPlatformWhatsAppOverview())

  const [isAgentOverviewLoaded, setIsAgentOverviewLoaded] = useState(false)

  const [conversations, setConversations] = useState<IPlatformWhatsAppConversation[]>([])

  const [selected, setSelected] = useState<IPlatformWhatsAppConversation | null>(null)

  const [messages, setMessages] = useState<IPlatformWhatsAppMessage[]>([])

  const [messageBody, setMessageBody] = useState("")

  const [search, setSearch] = useState("")

  const [settings, setSettings] = useState(emptyPlatformWhatsAppSettings())

  const [knowledge, setKnowledge] = useState<IPlatformWhatsAppKnowledge[]>([])

  const [showKnowledge, setShowKnowledge] = useState(false)

  const [editingKnowledge, setEditingKnowledge] = useState<IPlatformWhatsAppKnowledge | null>(null)

  const [isSettingsLoaded, setIsSettingsLoaded] = useState(false)

  const [showSettings, setShowSettings] = useState(false)

  const [showRespostaAiSettings, setShowRespostaAiSettings] = useState(false)

  const [showPairing, setShowPairing] = useState(false)

  const [managedAgentId, setManagedAgentId] = useState<number | null>(null)

  const [agentName, setAgentName] = useState(t("pairing.defaultName"))

  const [pairingResult, setPairingResult] = useState<IPlatformWhatsAppPairingResult | null>(null)

  const [isSavingLead, setIsSavingLead] = useState(false)

  const [savedLeadId, setSavedLeadId] = useState<number | null>(null)

  const isLeadSaved = selected !== null && savedLeadId === selected.id

  const isRespostaAiActive = settings.activeTransport === EWhatsAppTransportType.WhatsAppSaaS

  const respostaAiUrl = `${(settings.whatsAppSaaSBaseUrl || RESPOSTA_AI_DEFAULT_BASE_URL).replace(/\/+$/, "")}/login`

  const hasLinkedAgent = overview.agents.length > 0

  const canOpenPairing = isAgentOverviewLoaded && !hasLinkedAgent

  const managedAgent =
    managedAgentId === null ? null : (overview.agents.find((agent) => agent.id === managedAgentId) ?? null)

  const refresh = async (silent = false) => {
    const [nextOverview, nextConversations] = await Promise.all([api.getOverview(silent), api.getConversations(silent)])

    if (nextOverview) {
      setOverview(nextOverview)
      setIsAgentOverviewLoaded(true)
    }

    if (nextConversations) {
      setConversations(nextConversations)

      setSelected((current) => (current ? (nextConversations.find((item) => item.id === current.id) ?? null) : current))
    }
  }

  useEffect(() => {
    api
      .getSettings()
      .then((data) => {
        if (data) setSettings({ ...data, apiKey: "" })
      })
      .catch(() => undefined)
      .finally(() => setIsSettingsLoaded(true))
  }, [])

  useEffect(() => {
    if (!isSettingsLoaded || isRespostaAiActive) return
    refresh().catch(() => undefined)
    const interval = window.setInterval(() => refresh(true).catch(() => undefined), 10000)
    return () => window.clearInterval(interval)
  }, [isSettingsLoaded, isRespostaAiActive])

  useEffect(() => {
    if (!selected) return
    const loadMessages = () =>
      api
        .getMessages(selected.id, true)
        .then((data) => data && setMessages(data))
        .catch(() => undefined)
    loadMessages()
    const interval = window.setInterval(loadMessages, 5000)
    return () => window.clearInterval(interval)
  }, [selected?.id])

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase()
    return term
      ? conversations.filter((item) =>
          `${item.remoteName ?? ""} ${item.businessName ?? ""} ${item.remotePhone}`.toLowerCase().includes(term),
        )
      : conversations
  }, [conversations, search])

  const stageOptions = Object.values(EPlatformWhatsAppConversationStage)
    .filter((value) => typeof value === "number")
    .map((value) => ({ value: String(value), label: t(`stage.${value}`) }))

  const statusOptions = Object.values(EPlatformWhatsAppConversationStatus)
    .filter((value) => typeof value === "number")
    .map((value) => ({ value: String(value), label: t(`status.${value}`) }))

  const openSettings = async () => {
    const data = await api.getSettings()
    if (data) {
      setSettings({ ...data, apiKey: "" })
      setShowSettings(true)
    }
  }
  const openKnowledge = async () => {
    const data = await api.getKnowledge()

    if (data) {
      setKnowledge(data)
      setShowKnowledge(true)
    }
  }

  const editKnowledge = (document: IPlatformWhatsAppKnowledge) => {
    setShowKnowledge(false)
    setEditingKnowledge({ ...document })
  }
  const sendMessage = async () => {
    if (!selected || !messageBody.trim()) return

    await api.sendMessage(selected.id, messageBody)

    setMessageBody("")

    const data = await api.getMessages(selected.id, true)

    if (data) setMessages(data)
  }

  const changeSelectedConversation = (conversation: IPlatformWhatsAppConversation) => {
    setSavedLeadId(null)
    setSelected(conversation)
  }

  const saveLead = async () => {
    if (!selected || isSavingLead) return

    const conversation = selected

    setIsSavingLead(true)
    setSavedLeadId(null)

    try {
      await api.updateConversation(conversation)

      setSavedLeadId(conversation.id)

      await refresh(true).catch(() => undefined)
    } catch {
      setSavedLeadId(null)
    } finally {
      setIsSavingLead(false)
    }
  }

  return (
    <div className="space-y-4 p-2 md:p-4 lg:flex lg:h-full lg:min-h-0 lg:flex-col lg:gap-4 lg:space-y-0 lg:overflow-y-auto lg:overscroll-contain">
      <div className="flex flex-wrap items-center justify-between gap-3 rounded-2xl bg-gradient-to-r from-emerald-600 to-green-500 p-5 text-white shadow-lg lg:shrink-0">
        <div>
          <h2 className="text-xl font-black">{t("title")}</h2>

          <p className="text-sm text-emerald-50">{t("description")}</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {isRespostaAiActive ? (
            <>
              <a
                href={respostaAiUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-xl bg-white px-4 py-2 font-bold text-emerald-700"
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.openRespostaAi"
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} /> {t("actions.openRespostaAi")}
              </a>

              <button
                className="rounded-xl bg-white/15 px-4 py-2 font-bold hover:bg-white/25"
                onClick={openSettings}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.openSettings"
              >
                <FontAwesomeIcon icon={faGear} /> {t("actions.settings")}
              </button>

              <button
                className="rounded-xl bg-white/15 px-4 py-2 font-bold hover:bg-white/25"
                onClick={openKnowledge}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.openKnowledge"
              >
                <FontAwesomeIcon icon={faBook} /> {t("actions.knowledge")}
              </button>
            </>
          ) : (
            <>
              <button
                className="rounded-xl bg-white/15 px-4 py-2 font-bold hover:bg-white/25"
                onClick={() => refresh()}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.refresh"
              >
                <FontAwesomeIcon icon={faRefresh} /> {t("actions.refresh")}
              </button>

              <button
                type="button"
                disabled={!canOpenPairing}
                title={hasLinkedAgent ? t("pairing.revokeFirst") : undefined}
                className="rounded-xl bg-white/15 px-4 py-2 font-bold transition hover:bg-white/25 disabled:cursor-not-allowed disabled:opacity-45 disabled:hover:bg-white/15"
                onClick={() => {
                  if (!canOpenPairing) return

                  setPairingResult(null)
                  setShowPairing(true)
                }}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.revokeFirst"
              >
                <FontAwesomeIcon icon={faLink} /> {t("actions.pair")}
              </button>

              <button
                className="rounded-xl bg-white px-4 py-2 font-bold text-emerald-700"
                onClick={openSettings}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.openSettings"
              >
                <FontAwesomeIcon icon={faGear} /> {t("actions.settings")}
              </button>
            </>
          )}
        </div>
      </div>

      {isRespostaAiActive ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white p-10 text-center shadow-sm">
          <FontAwesomeIcon icon={faRobot} className="text-4xl text-emerald-600" />

          <h3 className="font-black text-slate-800">{t("respostaAi.activeTitle")}</h3>

          <p className="max-w-md text-sm text-slate-500">{t("respostaAi.activeDescription")}</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-[repeat(auto-fill,minmax(220px,1fr))] gap-2 p-1 lg:shrink-0">
            <MetricCard title={t("metrics.open")} value={`${overview.openConversations}`} icon={faComments} />

            <MetricCard title={t("metrics.handoff")} value={`${overview.awaitingHuman}`} icon={faClock} />

            <MetricCard title={t("metrics.qualified")} value={`${overview.qualifiedLeads}`} icon={faCircleCheck} />

            <MetricCard title={t("metrics.won")} value={`${overview.wonLeads}`} icon={faTrophy} />

            <MetricCard title={t("metrics.today")} value={`${overview.inboundMessagesToday}`} icon={faPaperPlane} />

            {overview.agents.length === 0 ? (
              <MetricCard title={t("metrics.agent")} value={t("offline")} icon={faRobot} color="#94a3b8" />
            ) : (
              overview.agents.map((agent) => (
                <MetricCard
                  key={agent.id}
                  title={t("metrics.agent")}
                  value={agent.isWhatsAppReady ? t("ready") : t("agent.pending")}
                  icon={faRobot}
                  color={agent.isWhatsAppReady ? "#059669" : "#d97706"}
                  ariaLabel={t("agent.manageLabel", { name: agent.name })}
                  onClick={() => setManagedAgentId(agent.id)}
                />
              ))
            )}
          </div>

          <div className="grid min-h-0 gap-4 lg:flex-1 lg:grid-cols-[330px_minmax(0,1fr)_310px] lg:overflow-hidden">
        <section className="flex h-[320px] min-h-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm sm:h-[400px] lg:h-full">
          <div className="shrink-0 border-b p-3">
            <InputField
              type="text"
              value={search}
              placeholder={t("inbox.search")}
              onChange={(event) => setSearch(event.target.value)}
              descriptionKey="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.input.search"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            {filtered.length === 0 && <p className="p-6 text-center text-sm text-slate-400">{t("inbox.empty")}</p>}

            {filtered.map((conversation) => (
              <button
                key={conversation.id}
                className={`w-full border-b p-4 text-left transition hover:bg-slate-50 ${selected?.id === conversation.id ? "bg-emerald-50" : ""}`}
                onClick={() => changeSelectedConversation(conversation)}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.changeSelectedConversationConversation"
              >
                <div className="flex justify-between gap-2">
                  <strong className="truncate text-sm text-slate-800">
                    {conversation.remoteName || conversation.businessName || conversation.remotePhone}
                  </strong>

                  {conversation.unreadCount > 0 && (
                    <span className="rounded-full bg-emerald-600 px-2 text-xs font-bold text-white">
                      {conversation.unreadCount}
                    </span>
                  )}
                </div>

                <p className="mt-1 truncate text-xs text-slate-500">{conversation.lastMessage}</p>

                <span className="mt-2 inline-block rounded-full bg-slate-100 px-2 py-1 text-[10px] font-bold text-slate-600">
                  {t(`stage.${conversation.stage}`)}
                </span>
              </button>
            ))}
          </div>
        </section>

        <section className="flex h-[70dvh] min-h-[420px] max-h-[720px] min-w-0 flex-col overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 shadow-sm lg:h-full lg:min-h-0 lg:max-h-none">
          {selected ? (
            <>
              <div className="shrink-0 border-b bg-white p-4">
                <h3 className="font-black text-slate-800">{selected.remoteName || selected.remotePhone}</h3>

                <p className="text-xs text-slate-500">{selected.remotePhone}</p>
              </div>
              <div className="min-h-0 flex-1 space-y-3 overflow-y-auto overscroll-contain p-4">
                {messages.map((message) => (
                  <div key={message.id} className={`flex ${message.direction === 1 ? "justify-end" : "justify-start"}`}>
                    <div
                      className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm ${message.direction === 1 ? "bg-emerald-600 text-white" : "bg-white text-slate-700"}`}
                    >
                      <p className="whitespace-pre-wrap">{message.body}</p>

                      <p
                        className={`mt-1 text-[10px] ${message.direction === 1 ? "text-emerald-100" : "text-slate-400"}`}
                      >
                        {new Date(message.createdAt).toLocaleString()}
                      </p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="flex items-center border-t bg-white p-3">
                <div className="w-[calc(100%-70px)] mr-2">
                  <InputField
                    type="text"
                    value={messageBody}
                    placeholder={t("inbox.reply")}
                    maxLength={4000}
                    onChange={(event) => setMessageBody(event.target.value)}
                    descriptionKey="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.input.reply"
                  />
                </div>

                <button
                  disabled={!messageBody.trim()}
                  onClick={sendMessage}
                  className="h-full rounded-xl bg-emerald-600 px-4 text-white disabled:opacity-50"
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.sendMessage"
                >
                  <FontAwesomeIcon icon={faPaperPlane} />
                </button>
              </div>
            </>
          ) : (
            <div className="flex h-full flex-col items-center justify-center p-8 text-center text-slate-400">
              <FontAwesomeIcon icon={faComments} className="mb-3 text-4xl" />
              <p>{t("inbox.select")}</p>
            </div>
          )}
        </section>

        <aside className="space-y-4 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm lg:h-full lg:min-h-0 lg:overflow-y-auto lg:overscroll-contain">
          <div className="flex items-center justify-between">
            <h3 className="font-black text-slate-800">{t("lead.title")}</h3>

            <FontAwesomeIcon icon={faRobot} className="text-emerald-600" />
          </div>

          {selected ? (
            <>
              <GenericDropDown
                legend={t("lead.stage")}
                initialState={String(selected.stage)}
                data={stageOptions}
                handlerFuncion={(event) =>
                  changeSelectedConversation({
                    ...selected,
                    stage: Number(event.target.value) as EPlatformWhatsAppConversationStage,
                  })
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.select.stage"
              />

              <GenericDropDown
                legend={t("lead.status")}
                initialState={String(selected.status)}
                data={statusOptions}
                handlerFuncion={(event) =>
                  changeSelectedConversation({
                    ...selected,
                    status: Number(event.target.value) as EPlatformWhatsAppConversationStatus,
                  })
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.select.status"
              />

              <InputField
                type="number"
                value={String(selected.leadScore)}
                placeholder={t("lead.score")}
                onChange={(event) =>
                  changeSelectedConversation({
                    ...selected,
                    leadScore: Math.min(100, Number(event.target.value) || 0),
                  })
                }
                descriptionKey="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.input.score"
              />

              <label className="block text-xs font-bold text-slate-600">
                {t("lead.summary")}
                <textarea
                  className="mt-2 w-full rounded-xl border-2 border-slate-200 p-3 text-sm"
                  rows={7}
                  value={selected.summary ?? ""}
                  onChange={(event) => changeSelectedConversation({ ...selected, summary: event.target.value })}
                  data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.input.input"
                />
              </label>

              <button
                type="button"
                disabled={isSavingLead}
                aria-busy={isSavingLead}
                className={`inline-flex w-full items-center justify-center gap-2 rounded-xl px-4 py-3 font-bold text-white transition-all duration-200 disabled:cursor-wait disabled:opacity-70 ${isLeadSaved ? "bg-emerald-600" : "bg-slate-900 hover:bg-slate-800"}`}
                onClick={saveLead}
                data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.saveLead"
              >
                {isSavingLead && <FontAwesomeIcon icon={faSpinner} spin />}

                {!isSavingLead && isLeadSaved && <FontAwesomeIcon icon={faCircleCheck} />}

                {isSavingLead ? t("actions.savingLead") : isLeadSaved ? t("actions.leadSaved") : t("actions.saveLead")}
              </button>

              {isLeadSaved && (
                <p role="status" aria-live="polite" className="text-center text-xs font-bold text-emerald-700">
                  {t("lead.saveSuccess")}
                </p>
              )}
            </>
          ) : (
            <p className="text-sm text-slate-400">{t("lead.empty")}</p>
          )}
          <div className="border-t pt-4">
            <button
              className="w-full rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 font-bold text-indigo-700"
              onClick={openKnowledge}
              data-interaction-description-key="interactionDescriptions:pages.adminDashboard.sections.platformWhatsAppSection.button.openKnowledge"
            >
              <FontAwesomeIcon icon={faBook} /> {t("actions.knowledge")}
            </button>
          </div>
        </aside>
      </div>
        </>
      )}

      {showSettings && (
        <PlatformWhatsAppSettingsPopup
          settings={settings}
          onChange={setSettings}
          onClose={() => setShowSettings(false)}
          onSave={async () => {
            const data = await api.saveSettings(settings)
            if (data) setSettings({ ...data, apiKey: "" })
            setShowSettings(false)
            await refresh(true)
          }}
          onOpenRespostaAiSettings={() => {
            setShowSettings(false)
            setShowRespostaAiSettings(true)
          }}
        />
      )}

      {showRespostaAiSettings && (
        <PlatformWhatsAppRespostaAiSettingsPopup
          settings={settings}
          onChange={setSettings}
          onClose={() => {
            setShowRespostaAiSettings(false)
            setShowSettings(true)
          }}
          onSave={async () => {
            const data = await api.saveSettings(settings)
            if (data) setSettings({ ...data, apiKey: "" })
            setShowRespostaAiSettings(false)
            setShowSettings(true)
            await refresh(true)
          }}
        />
      )}

      {showPairing && (
        <PlatformWhatsAppPairingPopup
          agentName={agentName}
          result={pairingResult}
          onAgentNameChange={setAgentName}
          onClose={() => setShowPairing(false)}
          onGenerate={async () => {
            const data = await api.generatePairingCode(agentName)
            if (data) setPairingResult(data)
            await refresh(true)
          }}
        />
      )}

      {managedAgent && (
        <PlatformWhatsAppAgentPopup
          agent={managedAgent}
          onClose={() => setManagedAgentId(null)}
          onRevoke={async () => {
            await api.deleteAgent(managedAgent.id)
            setManagedAgentId(null)
            await refresh(true)
          }}
        />
      )}

      {showKnowledge && (
        <PlatformWhatsAppKnowledgeListPopup
          documents={knowledge}
          onClose={() => setShowKnowledge(false)}
          onAdd={() => editKnowledge(emptyKnowledge())}
          onEdit={editKnowledge}
          onDelete={async (documentId) => {
            await api.deleteKnowledge(documentId)
            await openKnowledge()
          }}
        />
      )}

      {editingKnowledge && (
        <PlatformWhatsAppKnowledgePopup
          document={editingKnowledge}
          onChange={setEditingKnowledge}
          onClose={() => {
            setEditingKnowledge(null)
            setShowKnowledge(true)
          }}
          onSave={async () => {
            await api.saveKnowledge(editingKnowledge)
            setEditingKnowledge(null)
            await openKnowledge()
          }}
        />
      )}
    </div>
  )
}
